import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Plus, Trash2, RefreshCcw, Power } from "lucide-react";
import { api } from "@/lib/api";

export default function DiscountManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [amount, setAmount] = useState("10");
  const [maxUses, setMaxUses] = useState("10");
  const [minOrder, setMinOrder] = useState("30");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discount");
      setItems(data || []);
    } catch {
      toast.error("Failed to load discount codes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    const norm = code.trim().toUpperCase().replace(/\s+/g, "");
    if (!norm) { toast.error("Enter a code"); return; }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Amount must be > 0"); return; }
    const uses = parseInt(maxUses, 10);
    if (!Number.isFinite(uses) || uses < 1) { toast.error("Max uses must be ≥ 1"); return; }
    const minAmt = parseFloat(minOrder);
    if (!Number.isFinite(minAmt) || minAmt < 0) { toast.error("Min order must be ≥ 0"); return; }
    setCreating(true);
    try {
      await api.post("/discount", {
        code: norm,
        amount_off: amt,
        max_uses: uses,
        min_order_amount: minAmt,
        description: description.trim(),
      });
      toast.success(`Code ${norm} created`);
      setCode(""); setAmount("10"); setMaxUses("10"); setMinOrder("30"); setDescription("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code");
    } finally { setCreating(false); }
  };

  const remove = async (id, codeName) => {
    if (!confirm(`Delete code ${codeName}?`)) return;
    try {
      await api.delete(`/discount/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Code deleted");
    } catch { toast.error("Could not delete"); }
  };

  const toggle = async (id, current) => {
    try {
      await api.patch(`/discount/${id}`, { active: !current });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, active: !current } : i));
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="mt-10 card-soft p-6 md:p-8" data-testid="discount-manager">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><Tag className="h-5 w-5" /></span>
          <div>
            <h3 className="font-serif text-2xl text-sakura-ink">Discount Codes</h3>
            <p className="text-sm text-sakura-soft">Create promo codes customers enter on the payment page.</p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost" data-testid="discount-refresh"><RefreshCcw className="h-4 w-4" /> Refresh</button>
      </div>

      <form onSubmit={create} className="mt-5 grid md:grid-cols-6 gap-3 items-end" data-testid="discount-create-form">
        <label className="block md:col-span-1">
          <span className="text-sm font-semibold text-sakura-ink">Code</span>
          <input className="input-soft mt-1 uppercase" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SAKURA10" data-testid="discount-new-code" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-sakura-ink">$ Off</span>
          <input className="input-soft mt-1" type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="discount-new-amount" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-sakura-ink">Min Order</span>
          <input className="input-soft mt-1" type="number" min="0" step="1" value={minOrder} onChange={(e) => setMinOrder(e.target.value)} data-testid="discount-new-min" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-sakura-ink">Max Uses</span>
          <input className="input-soft mt-1" type="number" min="1" step="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="discount-new-uses" />
        </label>
        <label className="block md:col-span-1">
          <span className="text-sm font-semibold text-sakura-ink">Note</span>
          <input className="input-soft mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="optional" data-testid="discount-new-desc" />
        </label>
        <button type="submit" disabled={creating} className="btn-primary disabled:opacity-60" data-testid="discount-create-btn">
          <Plus className="h-4 w-4" /> {creating ? "Creating…" : "Create"}
        </button>
      </form>

      <div className="mt-6">
        {loading ? (
          <div className="py-10 text-center text-sakura-soft">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sakura-soft">No codes yet. Create one above — try <b>SAKURA10</b> for $10 off orders $30+.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="discount-table">
              <thead className="bg-sakura-100 text-sakura-ink">
                <tr className="text-left">
                  <th className="p-3">Code</th>
                  <th className="p-3">$ Off</th>
                  <th className="p-3">Min Order</th>
                  <th className="p-3">Used</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Note</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const remaining = Math.max(0, d.max_uses - d.used_count);
                  return (
                    <tr key={d.id} className="border-t border-sakura-200 hover:bg-sakura-50">
                      <td className="p-3 font-mono font-semibold text-sakura-ink tracking-wide">{d.code}</td>
                      <td className="p-3 text-sakura-ink">${d.amount_off.toFixed(2)}</td>
                      <td className="p-3 text-sakura-ink">{d.min_order_amount > 0 ? `$${d.min_order_amount.toFixed(0)}+` : "—"}</td>
                      <td className="p-3 text-sakura-ink">
                        <span className="font-semibold">{d.used_count}</span>
                        <span className="text-sakura-soft"> / {d.max_uses}</span>
                        <span className="text-xs text-sakura-soft ml-2">({remaining} left)</span>
                      </td>
                      <td className="p-3">
                        {d.active && remaining > 0 ? (
                          <span className="inline-block text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full px-2 py-1">Active</span>
                        ) : (
                          <span className="inline-block text-xs font-semibold bg-sakura-200 text-sakura-600 rounded-full px-2 py-1">{remaining === 0 ? "Used up" : "Paused"}</span>
                        )}
                      </td>
                      <td className="p-3 text-sakura-soft text-xs max-w-[200px] truncate">{d.description}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button onClick={() => toggle(d.id, d.active)} className="btn-ghost text-xs mr-2" data-testid={`discount-toggle-${d.id}`} title={d.active ? "Pause" : "Activate"}>
                          <Power className="h-3.5 w-3.5" /> {d.active ? "Pause" : "Activate"}
                        </button>
                        <button onClick={() => remove(d.id, d.code)} className="text-sakura-500 hover:text-sakura-600 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`discount-delete-${d.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
